import React from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  ResponsiveContainer,
  CartesianGrid,
  Tooltip,
} from "recharts";

interface DayBloodPressure {
  day: string;
  systolic: number | null;
  diastolic: number | null;
}

interface BloodPressureChartProps {
  data?: DayBloodPressure[];
  bare?: boolean;
}

const GREEN = "#22C55E";
const ORANGE = "#FF9130";

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-[8px] px-3 py-2 shadow-md text-xs font-inter">
      <p className="font-semibold text-[#1F1F1F] mb-1 text-[10px] tracking-wide">
        {String(label).slice(0, 2)}
      </p>
      {payload.map((p: any) => (
        <div key={p.name} className="flex items-center gap-1.5 mt-0.5">
          <span
            className="w-2 h-2 rounded-full inline-block flex-shrink-0"
            style={{ background: p.color }}
          />
          <span style={{ color: p.color }} className="font-medium">
            {p.name === "systolic" ? "Systolic" : "Diastolic"} · {p.value != null ? p.value : "—"}
          </span>
        </div>
      ))}
    </div>
  );
};

const EMPTY_BP_DATA: DayBloodPressure[] = [
  { day: "Mo", systolic: null, diastolic: null },
  { day: "Tu", systolic: null, diastolic: null },
  { day: "We", systolic: null, diastolic: null },
  { day: "Th", systolic: null, diastolic: null },
  { day: "Fr", systolic: null, diastolic: null },
  { day: "Sa", systolic: null, diastolic: null },
  { day: "Su", systolic: null, diastolic: null },
];

function computeBpYAxis(data: DayBloodPressure[]): { domain: [number, number]; ticks: number[] } {
  const values: number[] = [];
  data.forEach((d) => {
    if (d.systolic != null) values.push(d.systolic);
    if (d.diastolic != null) values.push(d.diastolic);
  });

  if (values.length === 0) {
    return { domain: [60, 140], ticks: [60, 80, 100, 120, 140] };
  }

  const rawMin = Math.min(...values);
  const rawMax = Math.max(...values);

  // Pick a step that yields ~4 intervals across the range (min 10, in multiples of 10).
  const span = Math.max(rawMax - rawMin, 40);
  const rawStep = span / 4;
  const step = Math.max(10, Math.ceil(rawStep / 10) * 10);

  const min = Math.floor(rawMin / step) * step;
  const max = Math.ceil(rawMax / step) * step;
  const domainMax = max === min ? min + step : max;

  const ticks: number[] = [];
  for (let v = min; v <= domainMax; v += step) ticks.push(v);

  return { domain: [min, domainMax], ticks };
}

const BloodPressureChart: React.FC<BloodPressureChartProps> = ({
  data = [],
  bare = false,
}) => {
  // Ensure axes always render — feed a 7-day null placeholder when empty.
  const displayData = data.length > 0 ? data : EMPTY_BP_DATA;
  const { domain, ticks } = computeBpYAxis(displayData);

  // A single-point line has no segment to render — show a dot for any value
  // whose neighbours on the same series are null, so isolated days stay visible.
  const makeIsolatedDot = (dataKey: "systolic" | "diastolic", color: string, radius: number) =>
    (props: any) => {
      const { cx, cy, index } = props;
      const curr = displayData[index]?.[dataKey];
      const prev = index > 0 ? displayData[index - 1]?.[dataKey] : null;
      const next = index < displayData.length - 1 ? displayData[index + 1]?.[dataKey] : null;
      if (curr != null && prev == null && next == null) {
        return <circle cx={cx} cy={cy} r={radius} fill={color} />;
      }
      return null;
    };

  const chart = (
    <ResponsiveContainer width="100%" height={140}>
      <LineChart data={displayData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
        <CartesianGrid vertical={false} stroke="#F3F4F6" strokeDasharray="0" />
        <XAxis
          dataKey="day"
          axisLine={false}
          tickLine={false}
          tick={{ fontSize: 12, fontFamily: "Inter, sans-serif", fill: "#8D8D8D" }}
          dy={6}
        />
        <YAxis
          width={30}
          axisLine={false}
          tickLine={false}
          tick={{ fontSize: 12, fontFamily: "Inter, sans-serif", fill: "#8D8D8D" }}
          domain={domain}
          ticks={ticks}
          allowDataOverflow
        />
        <Tooltip content={<CustomTooltip />} cursor={{ stroke: "#E5E7EB", strokeDasharray: "3 3" }} />
        <Line
          type="monotone"
          dataKey="systolic"
          name="systolic"
          stroke={GREEN}
          strokeWidth={2.5}
          dot={makeIsolatedDot("systolic", GREEN, 5)}
          activeDot={{ r: 5, fill: GREEN, strokeWidth: 2, stroke: "#FFFFFF" }}
          connectNulls
          isAnimationActive={true}
          animationBegin={0}
          animationDuration={1000}
          animationEasing="ease-out"
        />
        <Line
          type="monotone"
          dataKey="diastolic"
          name="diastolic"
          stroke={ORANGE}
          strokeWidth={2.5}
          dot={makeIsolatedDot("diastolic", ORANGE, 5)}
          activeDot={{ r: 5, fill: ORANGE, strokeWidth: 2, stroke: "#FFFFFF" }}
          connectNulls
          isAnimationActive={true}
          animationBegin={200}
          animationDuration={1000}
          animationEasing="ease-out"
        />
      </LineChart>
    </ResponsiveContainer>
  );

  if (bare) return chart;

  return (
    <div className="bg-white border border-[#F3F4F6] rounded-xl p-4 flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <span className="text-[12px] 3xl:text-[14px] font-semibold uppercase tracking-widest text-[#1F1F1F] font-inter">
          Blood Pressure <span className="normal-case tracking-normal text-[#8E8E93] font-normal">(mmHg)</span>
        </span>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: GREEN }} />
            <span className="text-[11px] text-[#1F1F1F] font-inter">Systolic</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: ORANGE }} />
            <span className="text-[11px] text-[#1F1F1F] font-inter">Diastolic</span>
          </div>
        </div>
      </div>

      {/* Chart */}
      <div className="flex-1">{chart}</div>
    </div>
  );
};

export default BloodPressureChart;
